export type Dir = 0 | 1 | 2 | 3; // up, right, down, left
export const DIR_UP: Dir = 0;
export const DIR_RIGHT: Dir = 1;
export const DIR_DOWN: Dir = 2;
export const DIR_LEFT: Dir = 3;
export const DX = [0, 1, 0, -1] as const;
export const DY = [-1, 0, 1, 0] as const;
export const isOpposite = (a: Dir, b: Dir) => (a + 2) % 4 === b;

export type GameMode = 'ffa' | 'teams';

export const FOOD_NORMAL = 0;
export const FOOD_GOLDEN = 1;
export const FOOD_REMAINS = 2; // dropped by a dead snake
/** Power-ups (only spawn when `rules.powerups` is on). */
export const FOOD_GHOST = 3; // pass through other snakes
export const FOOD_DOUBLE = 4; // double points
export const FOOD_MAGNET = 5; // also eat food on the 8 cells around the head
export const FOOD_SPEED = 6; // move an extra cell every other tick
export const FOOD_ARMOR = 7; // survive one crash (the snake bounces off instead)
export const FOOD_FREEZE = 8; // rivals near your head stop moving for a moment
export const FOOD_TELEPORT = 9; // the next move warps the head to a safe open spot
/** Capture the food: the trophy is carried back to your team's base (never a power-up, never vacuumed). */
export const FOOD_TROPHY = 10;
/** Dropped by a big snake when it dies: worth more and drawn bigger. */
export const FOOD_REMAINS_BIG = 11;
/** Electric shock (only with `rules.electric`): every rival snake is cut to half its length; scores stay. */
export const FOOD_ELECTRIC = 12;
/** Fire (only with `rules.fire`): for a while the snake burns, and any rival it touches dies. */
export const FOOD_FIRE = 13;
export type FoodKind = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
export const FOOD_VALUE: Record<FoodKind, number> = { 0: 1, 1: 5, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1, 7: 1, 8: 1, 9: 1, 10: 0, 11: 3, 12: 1, 13: 1 };
export const POWERUP_KINDS = [FOOD_GHOST, FOOD_DOUBLE, FOOD_MAGNET, FOOD_SPEED, FOOD_ARMOR, FOOD_FREEZE, FOOD_TELEPORT] as const;
export const isPowerup = (k: FoodKind) => (k >= FOOD_GHOST && k <= FOOD_TELEPORT) || k === FOOD_ELECTRIC || k === FOOD_FIRE;
/** Plain food a magnet may pull in. */
export const isEdible = (k: FoodKind) => k <= FOOD_REMAINS || k === FOOD_REMAINS_BIG;

/** Active power-up effects as a bitmask (also the wire format). */
export const EFFECT_GHOST = 1;
export const EFFECT_DOUBLE = 2;
export const EFFECT_MAGNET = 4;
/** Spawn protection: intangible like a ghost, shown differently. */
export const EFFECT_SHIELD = 8;
export const EFFECT_SPEED = 16;
/** Holding an armor charge (consumed by the next crash). */
export const EFFECT_ARMOR = 32;
export const EFFECT_FROZEN = 64;
/** Capture the food: carrying the trophy. */
export const EFFECT_CARRY = 128;
/**
 * Fire power-up: the snake is burning. It doesn't fit in the wire's effects byte, so it travels
 * as a player flag bit (see protocol/binary.ts) and is merged back in here on the client.
 */
export const EFFECT_FIRE = 256;

/** `fire`: touched by a snake with the fire power-up. */
export type DeathCause = 'wall' | 'self' | 'snake' | 'head_on' | 'disconnect' | 'zone' | 'fire';

/**
 * Extra points for a moment worth celebrating:
 * - revenge:    killing the snake that last killed you (respawn arenas)
 * - bounty:     killing the snake that was leading on score (free-for-all respawn arenas)
 * - close_call: turning away at the last moment from a rival's body that was right in front of you
 */
export type BonusKind = 'revenge' | 'bounty' | 'close_call' | 'boss';
/** Sent by index: add new kinds at the end. */
export const BONUS_KINDS: readonly BonusKind[] = ['revenge', 'bounty', 'close_call', 'boss'];

export interface GameConfig {
  width: number;
  height: number;
  /** Simulation ticks per second. Each tick a snake advances exactly one cell. */
  tickRate: number;
  /** Hard time limit in ticks. When reached, the highest score among survivors wins. */
  durationTicks: number;
  mode: GameMode;
  /** Number of normal food items kept on the board at all times. */
  foodTarget: number;
  startLength: number;
  seed: number;
  /** Ticks before the first move, for the on-screen countdown. */
  countdownTicks: number;
  /** Optional rule modifiers (used by single-player modes). All off by default. */
  rules?: GameRules;
}

export interface GameRules {
  /** Leaving one edge enters from the opposite edge instead of dying. */
  wrap?: boolean;
  /** Eating again within the combo window raises a score multiplier (up to MAX_MULTIPLIER). */
  combo?: boolean;
  /** A rock appears after every few apples eaten, making long runs progressively harder. */
  obstacles?: boolean;
  /** Power-ups (ghost, double points, magnet) spawn on the board. */
  powerups?: boolean;
  /** Battle royale: the playable area shrinks over time; leaving it is fatal. */
  shrink?: boolean;
  /** Eliminated snakes come back after a short delay (score is kept); the match runs on the clock. */
  respawn?: boolean;
  /** Snakes are intangible for a few seconds after (re)spawning. */
  spawnShield?: boolean;
  /** Multiply every point awarded (big arena: 100 per apple, so scores feel big). Always a whole number. */
  pointScale?: number;
  /** Big arena: bots earn this share of food and kill points and restart from 0 on respawn; all but the boss stop growing at this length. */
  botHandicap?: { pointShare: number; maxLength: number };
  /** King of the hill: holding the hill alone scores points; the hill moves every so often. */
  hill?: boolean;
  /** Capture the food (teams): carry the trophy from the middle into your base. */
  ctf?: boolean;
  /** Players may hold boost: 1.5x speed, paid for with length (food is dropped behind). */
  boost?: boolean;
  /** Most power-ups on the board at once (default 2), and the per-tick chance one spawns (default 1/70). */
  maxPowerups?: number;
  powerupChance?: number;
  /** How long ghost lasts once eaten, in ticks (default POWERUP_DURATION_TICKS). */
  ghostTicks?: number;
  /** Magnet reach in cells around the head (default 1, i.e. the 8 surrounding cells). */
  magnetRadius?: number;
  /** The electric shock power-up can spawn. */
  electric?: boolean;
  /** The fire power-up can spawn. */
  fire?: boolean;
  /** Big arena: this week's event (its changes are already applied to the config; this is for display). */
  event?: ArenaEventId;
}

/** Weekly big-arena events: double food, a power-up frenzy, or every snake starting giant. */
export type ArenaEventId = 'feast' | 'frenzy' | 'giants';

export interface PlayerInit {
  slot: number;
  userId: string;
  name: string;
  team: number; // 0 in FFA, 0/1 in team mode
  skin: string;
  isBot?: boolean;
  /** Respawn arenas: lives for this player (null/absent = unlimited, e.g. bots). */
  lives?: number | null;
  /** Respawn arenas: the boss, a giant bot worth a big bonus that comes back only after a long wait. */
  boss?: boolean;
}

export interface PlayerState extends PlayerInit {
  alive: boolean;
  /** Body cells, head first. A cell is y * width + x. */
  body: number[];
  dir: Dir;
  /** Up to two buffered turns so quick "down, left" inputs are not lost between ticks. */
  pendingDirs: Dir[];
  grow: number;
  score: number;
  kills: number;
  foodEaten: number;
  maxLength: number;
  deathTick: number | null;
  deathCause: DeathCause | null;
  killedBy: number | null;
  /** Current combo multiplier (1 when combos are off or the window lapsed). */
  multiplier: number;
  maxMultiplier: number;
  /** Tick until which the next apple continues the combo. */
  comboUntil: number;
  /** Tick until which each power-up effect lasts (0 = never had it). */
  ghostUntil: number;
  doubleUntil: number;
  magnetUntil: number;
  speedUntil: number;
  frozenUntil: number;
  /** Fire power-up: burning until this tick. */
  fireUntil: number;
  /** One armor charge: the next crash bounces the snake instead of killing it. */
  armor: boolean;
  /** The next move warps the head to a safe spot. */
  teleportPending: boolean;
  powerupsTaken: number;
  /** Capture the food: carrying the trophy, and trophies delivered. */
  carrying: boolean;
  captures: number;
  /** King of the hill: ticks spent holding the hill alone. */
  hillTicks: number;
  /** Boost held down (only acts with `rules.boost`), and ticks boosted so far (for its cost). */
  boosting: boolean;
  boostTicks: number;
  /** Spawn protection lasts until this tick. */
  shieldUntil: number;
  /** With `rules.respawn`: tick at which a dead snake comes back (null = not coming back). */
  respawnAt: number | null;
  deaths: number;
  /** Lives remaining (null = unlimited). At 0 the snake is out until its seat is restarted. */
  livesLeft: number | null;
  /** Respawn arenas: the slot that last killed this snake (killing it back pays a revenge bonus). */
  nemesis: number | null;
  /** Tick of the last close-call bonus (they have a cooldown). */
  lastCloseCall: number;
}

export interface Food {
  cell: number;
  kind: FoodKind;
  /** Tick at which golden food disappears; null for permanent food. */
  expiresAt: number | null;
}

export type GamePhase = 'countdown' | 'running' | 'finished';

/** Everything that changed during one tick. Drives delta network encoding and client effects. */
export interface TickEvents {
  tick: number;
  moved: Array<{ slot: number; head: number; tailPopped: boolean }>;
  /** Second move in the same tick for snakes with a speed boost (applied after `moved`). */
  extraMoves: Array<{ slot: number; head: number; tailPopped: boolean }>;
  died: Array<{ slot: number; cause: DeathCause; killedBy: number | null }>;
  foodAdded: Food[];
  foodRemoved: number[];
  eaten: Array<{ slot: number; cell: number; kind: FoodKind; points: number; multiplier: number }>;
  obstaclesAdded: number[];
  /** Snakes that came back this tick, with their fresh bodies. */
  respawned: Array<{ slot: number; body: number[]; dir: Dir }>;
  /** Boost cost: these snakes lost their last tail segment this tick (after all moves). */
  trimmed: number[];
  /** Electric shock: these snakes were cut down; `length` is their body length at the end of the tick. */
  shocked: Array<{ slot: number; length: number }>;
  /** Bonus points awarded this tick (already included in the snakes' scores). */
  bonuses: Array<{ slot: number; kind: BonusKind; points: number }>;
  finished: boolean;
}

export interface Standing {
  slot: number;
  userId: string;
  name: string;
  team: number;
  place: number;
  score: number;
  kills: number;
  maxLength: number;
  foodEaten: number;
  survivedTicks: number;
  deathCause: DeathCause | null;
  isBot: boolean;
  /** Capture the food: trophies delivered (0 elsewhere). */
  captures?: number;
}

export interface GameResult {
  winnerSlots: number[];
  winningTeam: number | null;
  standings: Standing[];
  ticks: number;
}
